/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ControlledInput,
  ControlledTextarea,
  FormInput,
  FormUpload,
  PrimaryButton,
  RedButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useCommunities } from "../../store/useCommunities";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useAlert } from "../../store/useAlert";
import { useCommunityWithdraws } from "../../store/useCommunityWithdraws";
import { CommunityWithdrawTable } from "./CommunityWithdrawTable";
import { useUsers } from "../../store/useUsers";
import { CommunityUsersTable } from "./CommunityUsersTable";
import { Switch } from "@material-tailwind/react";
import { useDirectUploadImage } from "../../store/useDirectUploadImage";
import {
  DropLocationInputItem,
  DynamicFormInput,
} from "../../components/form/DynamicFormInput";
import { useDropLocations } from "../../store/useDropLocations";

type Inputs = {
  name: string;
  description: string;
  website: string;
};

function EditCommunityScreen() {
  /** Params */
  const params = useParams();
  /** Hooks */
  const { createWithdraw, getWithdraws, withdraws } = useCommunityWithdraws();
  const { setAlert } = useAlert();
  const {
    updateCommunity,
    updatedCommunity,
    clearUpdatedCommunity,
    getCommunity,
    community,
  } = useCommunities();
  const navigate = useNavigate();
  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  const {
    uploadImage,
    clearUploadedImage,
    uploadedImage,
    setUploadedImage,
    thumbnailImage,
    setThumbnailImage,
  } = useDirectUploadImage();
  const {
    uploadImage: uploadBackgroundImage,
    clearUploadedImage: clearUploadedBackgroundImage,
    uploadedImage: uploadedBackgroundImage,
    setUploadedImage: setUploadedBackgroundImage,
  } = useDirectUploadImage();
  const { allCommunityUsers, getUsersByCommunity } = useUsers();
  const { dropLocations, getDropLocations } = useDropLocations();
  /** State */
  const [image, setImage] = useState<File | null>(null);
  const [featuredImage, setFeaturedImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [modal, setShowModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState<string>("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [cities, setCities] = useState<DropLocationInputItem[]>([]);

  /** Effects */
  useEffect(() => {
    if (updatedCommunity) {
      clearUploadedImage();
      clearUploadedBackgroundImage();
      clearUpdatedCommunity();
      navigate(-1);
    }
  }, [updatedCommunity]);

  useEffect(() => {
    getCommunity(params?.id as string);
    getWithdraws(params?.id as string, 1, 500);
    getUsersByCommunity(params?.id as string);
    getDropLocations();
    return () => {
      setCities([]);
    };
  }, []);

  useEffect(() => {
    if (community) {
      reset({
        name: community.name,
        description: community.description,
        website: community.website,
      });
      setCities(community?.cities?.map((ct) => ({ id: ct, value: ct })) ?? []);
      setIsFeatured(community?.isFeatured ?? false);
      setUploadedImage(community?.image);
      setUploadedBackgroundImage(community.featuredImage ?? null);
      setThumbnailImage(community?.thumbnail ?? community?.image);
    }
  }, [community]);

  useEffect(() => {
    if (image) {
      onUploadImage();
    }
  }, [image]);

  useEffect(() => {
    if (featuredImage) {
      onUploadBackgroundImage();
    }
  }, [featuredImage]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const onSubmit = async (data: Inputs) => {
    if (!uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!uploadedBackgroundImage) {
      setAlert({
        isOpen: true,
        message: "Please upload a featured background image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    setSubmitted(false);
    let body: any = {
      ...data,
      image: uploadedImage,
      thumbnail: thumbnailImage ?? uploadedImage,
      isFeatured,
    };
    if (uploadedBackgroundImage) {
      body.featuredImage = uploadedBackgroundImage;
    }
    if (cities?.length) {
      body.cities = cities?.map((input) => input?.id) ?? [];
    }
    updateCommunity(params?.id as string, body);
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image, true);
  };

  const imageError = submitted && !uploadedImage;

  const onBackgroundImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setFeaturedImage(e.target.files[0]);
    }
  };

  const onUploadBackgroundImage = () => {
    if (!featuredImage) return;
    uploadBackgroundImage(featuredImage);
  };

  const backgroundImageError = submitted && !featuredImage;

  const disabledPayout = useMemo(() => {
    return Number(community?.balance ?? 0) <= 0;
  }, [community?.balance]);

  const initializePayout = () => {
    if (!payoutAmount) {
      setAlert({
        isOpen: true,
        message: "Please enter an amount.",
        severity: "error",
      });
      return;
    }
    createWithdraw({
      amount: Math.round(Number(payoutAmount ?? 0) * 100),
      community: community?._id,
    });
    setPayoutAmount("");
    setShowModal(false);
  };

  const citiesToSelect = useMemo(() => {
    if (!dropLocations?.length) return [];
    let locationsToSelect: { value: string; label: string }[] = [];
    for (let location of dropLocations) {
      if (locationsToSelect?.some((loc) => loc?.value === location?.city))
        continue;
      locationsToSelect.push({
        label: location?.city as string,
        value: location?.city as string,
      });
    }
    return locationsToSelect;
  }, [dropLocations]);

  return (
    <div>
      <div className="flex flex-row items-center justify-between mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Edit community
        </h1>
        <PrimaryButton
          disabled={disabledPayout}
          label="Payout"
          onClick={() => setShowModal(true)}
        />
      </div>
      {/* Top Stats */}
      <div className="flex flex-col sm:flex-row w-full p-2 sm:p-7 bg-white border border-gray-border drop-shadow-md sm:h-28 mb-1 sm:mb-10">
        <div className="flex flex-1 sm:flex-col justify-between items-center  border-b sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
          <div className="flex items-center mb-1">
            <p className="font-satoshi text-2xl text-dark font-bold">
              €{Number(Number(community?.totalRevenue ?? 0) / 100)?.toFixed(2)}
            </p>
          </div>
          <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
            Total Pay-out
          </p>
        </div>
        <div className="flex flex-1 sm:flex-col justify-between items-center  border-b sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
          <div className="flex items-center mb-1">
            <p className="font-satoshi text-2xl text-dark font-bold">
              €{Number(Number(community?.balance ?? 0) / 100)?.toFixed(2)}
            </p>
          </div>
          <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
            Current balance
          </p>
        </div>
        <div className="flex flex-1 sm:flex-col justify-between items-center  border-b sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
          <div className="flex items-center mb-1">
            <p className="font-satoshi text-2xl text-dark font-bold">
              {community?.members}
            </p>
          </div>
          <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
            Total members
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md mb-2 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Update Community details
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              error={Boolean(errors?.name)}
              label="Name"
              placeholder="Community name"
              name="name"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.website)}
              label="Website"
              placeholder="Community website"
              name="website"
              rules={{
                required: true,
                pattern: /https?:\/\/.+/,
              }}
            />
            {/* Lottery prize image */}
            <FormUpload
              label="Community logo"
              onChange={onImageChange}
              value={image}
              error={imageError}
              imageUrl={uploadedImage as string}
            />
            <Switch
              crossOrigin={"anonymous"}
              color="teal"
              ripple
              label="Is featured"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e?.target.checked)}
            />
            {/* Community featured backround image */}
            <FormUpload
              label="Featured background image"
              onChange={onBackgroundImageChange}
              value={featuredImage}
              error={backgroundImageError}
              imageUrl={uploadedBackgroundImage as string}
            />
            <ControlledTextarea
              name="description"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.description)}
              label="Description"
              placeholder="Type community description here"
              inputClassName="min-h-[180px]"
              labelClassName="mt-4"
            />
            <DynamicFormInput
              generateIds={false}
              label="Cities"
              inputs={cities}
              setInputs={setCities}
              options={citiesToSelect ?? []}
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
      </div>
      {/* Payout modal */}
      {modal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-10/12 sm:w-1/4 my-0 sm:my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-start justify-center p-5 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-3xl font-semibold font-satoshi text-dark">
                    Payout to {community?.name}
                  </h3>
                </div>
                {/*body*/}
                <div className="relative p-6 flex-auto">
                  <p className="font-satoshi text-dark font-extrabold text-3xl text-center">
                    Balance:{" "}
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(Number(community?.balance ?? 0) / 100)}
                  </p>
                  <FormInput
                    inputClassName="mt-4"
                    placeholder="Enter amount"
                    type="number"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                  />
                  {Math.round(Number(payoutAmount ?? 0) * 100) >
                  Number(community?.balance ?? 0) ? (
                    <p className="font-satoshi text-red-600 font-medium">
                      Maximum amount to withdraw is:{" "}
                      {new Intl.NumberFormat("nl-NL", {
                        style: "currency",
                        currency: "EUR",
                      }).format(Number(community?.balance ?? 0) / 100)}
                    </p>
                  ) : null}
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <RedButton
                    label="Cancel"
                    className="mr-4"
                    onClick={() => {
                      setPayoutAmount("");
                      setShowModal(false);
                    }}
                  />
                  <PrimaryButton
                    disabled={
                      !payoutAmount ||
                      payoutAmount === "" ||
                      Math.round(Number(payoutAmount ?? 0) * 100) >
                        Number(community?.balance ?? 0)
                    }
                    label="Payout"
                    onClick={initializePayout}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}

      <div className="mt-12">
        <div className="flex w-full justify-between mb-3">
          {community?.name}'s Withdraws
        </div>
        <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
          {!withdraws?.length ? (
            <p className="text-center font-satoshi text-base text-dark">
              There is no withdraw created yet.
            </p>
          ) : (
            <CommunityWithdrawTable communityId={params?.id as string} />
          )}
        </div>
      </div>

      <div className="mt-12">
        <div className="flex w-full justify-between mb-3">
          Community members
        </div>
        <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
          {!allCommunityUsers?.length ? (
            <p className="text-center font-satoshi text-base text-dark">
              This community does not have any member yet.
            </p>
          ) : (
            <CommunityUsersTable />
          )}
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(EditCommunityScreen);
